import type {
  BaseAppearance,
  BaseBackground,
  BaseBorder,
  BaseControlTone,
  BaseDensity,
  BaseHeadingSize,
  BaseLayoutAlign,
  BaseLayoutJustify,
  BaseLayoutLength,
  BaseListMarker,
  BaseListSpacing,
  BaseRadius,
  BaseResolvedTheme,
  BaseThemePresetId,
  BaseThemeSlots,
  BaseThemeStyles,
  BaseTypographySize,
  BaseTypographyTone,
} from './types';
import { cn } from './utils';

type ResolvedSlots = Required<BaseThemeSlots>;

const emptyStyles: BaseThemeStyles = {};

const motionBase =
  'motion-safe:transition motion-safe:duration-300 motion-safe:ease-out';

const motionInteractive =
  'motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out';

const motionTransform =
  'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out';
const tableScrollbarChrome = cn(
  '[scrollbar-width:thin]',
  '[scrollbar-color:hsl(var(--muted-foreground)/0.45)_transparent]',
  '[&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar]:w-2',
  '[&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-button]:h-0 [&::-webkit-scrollbar-button]:w-0 [&::-webkit-scrollbar-button]:bg-transparent',
  '[&::-webkit-scrollbar-button:single-button]:hidden [&::-webkit-scrollbar-button:single-button]:h-0 [&::-webkit-scrollbar-button:single-button]:w-0',
  '[&::-webkit-scrollbar-button:double-button]:hidden [&::-webkit-scrollbar-button:double-button]:h-0 [&::-webkit-scrollbar-button:double-button]:w-0',
  '[&::-webkit-scrollbar-corner]:bg-transparent',
  '[&::-webkit-scrollbar-track]:bg-transparent',
  '[&:has(thead.sticky)::-webkit-scrollbar-track]:mt-[var(--base-table-header-height)]',
  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/40',
  'hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/55',
);
const tableStickyHeaderCells = cn(
  '[&.sticky_th]:sticky [&.sticky_th]:top-0 [&.sticky_th]:z-[2]',
  '[&.sticky_th]:bg-[var(--base-table-sticky-header-bg)]',
  '[&.sticky_th]:shadow-[inset_0_-1px_0_var(--base-table-header-separator)]',
);

const baseSlots: ResolvedSlots = {
  buttonBase:
    cn(
      'group/button inline-flex min-w-0 items-center justify-center gap-2 rounded-md border px-3 text-sm font-medium focus-visible:outline-none',
      'cursor-pointer disabled:!cursor-not-allowed',
      motionInteractive,
    ),
  buttonDefault:
    'border-border bg-background text-foreground hover:bg-accent/60',
  buttonPrimary:
    'border-primary/20 bg-primary/10 text-primary hover:bg-primary/15',
  buttonSuccess:
    'border-[hsl(var(--success,142_72%_29%)_/_0.30)] bg-[hsl(var(--success,142_72%_29%)_/_0.10)] text-[hsl(var(--success,142_72%_29%))] hover:bg-[hsl(var(--success,142_72%_29%)_/_0.15)]',
  buttonWarning:
    'border-[hsl(var(--warning,45_93%_47%)_/_0.30)] bg-[hsl(var(--warning,45_93%_47%)_/_0.10)] text-[hsl(var(--warning-foreground,28_73%_26%))] hover:bg-[hsl(var(--warning,45_93%_47%)_/_0.15)]',
  buttonDanger:
    'border-destructive/20 bg-destructive/10 text-destructive-text hover:bg-destructive/15',
  buttonMuted:
    'border-border/60 bg-muted/45 text-muted-foreground hover:bg-muted/70 hover:text-foreground',
  buttonActive: 'border-primary/35 bg-primary/10 text-primary',
  buttonDisabled: '!cursor-not-allowed opacity-60',
  buttonIconOnly: 'w-9 px-0',
  buttonIcon:
    'inline-flex h-4 w-4 shrink-0 items-center justify-center text-[inherit] [&_*]:!text-[inherit] [&_svg]:!stroke-current [&_svg_*]:!stroke-current',
  buttonLabel: 'min-w-0 truncate',
  badgeBase:
    cn(
      'inline-flex max-w-full items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
      motionBase,
    ),
  badgeDefault: 'border-border/60 bg-background text-foreground',
  badgePrimary: 'border-primary/30 bg-primary/10 text-primary',
  badgeSuccess: 'border-[hsl(var(--success,142_72%_29%)_/_0.30)] bg-[hsl(var(--success,142_72%_29%)_/_0.10)] text-[hsl(var(--success,142_72%_29%))]',
  badgeWarning: 'border-[hsl(var(--warning,45_93%_47%)_/_0.30)] bg-[hsl(var(--warning,45_93%_47%)_/_0.10)] text-[hsl(var(--warning-foreground,28_73%_26%))]',
  badgeDanger: 'border-destructive/30 bg-destructive/10 text-destructive-text',
  badgeMuted: 'border-border/60 bg-muted text-muted-foreground',
  badgeIcon: 'inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center',
  badgeLabel: 'min-w-0 truncate',
  textRoot: 'min-w-0 break-words leading-6',
  textSizeXs: 'text-xs',
  textSizeSm: 'text-sm',
  textSizeMd: 'text-base',
  textSizeLg: 'text-lg',
  textSizeXl: 'text-xl',
  typographyToneDefault: 'text-foreground',
  typographyToneMuted: 'text-muted-foreground',
  typographyTonePrimary: 'text-primary',
  typographyToneSuccess: 'text-emerald-700 dark:text-emerald-400',
  typographyToneWarning: 'text-amber-700 dark:text-amber-400',
  typographyToneDanger: 'text-destructive-text',
  typographyToneInherit: 'text-inherit',
  headingRoot: 'min-w-0 break-words font-semibold tracking-tight',
  headingSizeXs: 'text-sm leading-5',
  headingSizeSm: 'text-base leading-6',
  headingSizeMd: 'text-lg leading-7',
  headingSizeLg: 'text-xl leading-7',
  headingSizeXl: 'text-2xl leading-8',
  headingSize2xl: 'text-3xl leading-9',
  linkRoot:
    'inline break-words underline decoration-current/40 underline-offset-4 hover:decoration-current focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
  codeInline:
    'rounded border border-border/70 bg-muted/60 px-1.5 py-0.5 font-mono text-[0.875em] leading-[1.45] text-foreground',
  codeBlock:
    'm-0 max-w-full overflow-x-auto rounded-md border border-border/70 bg-muted/60 p-4 text-foreground',
  codeContent: 'font-mono text-sm leading-6',
  kbdRoot:
    'inline-flex min-h-5 items-center rounded border border-border bg-muted/70 px-1.5 font-mono text-[0.75em] font-medium leading-5 text-foreground shadow-[inset_0_-1px_0_hsl(var(--monkeys-color-border))]',
  listRoot: 'm-0 min-w-0 list-outside break-words ps-5 text-foreground',
  listSpacingCompact: '[&>li+li]:mt-1',
  listSpacingDefault: '[&>li+li]:mt-2',
  listSpacingRelaxed: '[&>li+li]:mt-3',
  listMarkerNone: 'list-none ps-0',
  listMarkerDisc: 'list-disc',
  listMarkerCircle: 'list-[circle]',
  listMarkerSquare: 'list-[square]',
  listMarkerDecimal: 'list-decimal',
  listMarkerLowerAlpha: 'list-[lower-alpha]',
  listMarkerUpperAlpha: 'list-[upper-alpha]',
  listItem: 'min-w-0 ps-1 leading-6 marker:text-muted-foreground',
  stackRoot: 'flex min-w-0 flex-col',
  inlineRoot: 'flex min-w-0 flex-row',
  gridRoot: 'grid min-w-0',
  containerRoot: 'mx-auto w-full min-w-0',
  aspectRatioRoot:
    'relative min-w-0 overflow-hidden [&>*]:h-full [&>*]:w-full',
  spinnerRoot:
    'inline-flex shrink-0 items-center justify-center text-current align-middle',
  spinnerIndicator:
    'h-5 w-5 rounded-full border-2 border-current border-r-transparent motion-safe:animate-spin motion-reduce:animate-none',
  visuallyHiddenRoot: 'sr-only',
  visuallyHiddenFocusable:
    'focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[1000] focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-foreground focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-ring focus-within:not-sr-only focus-within:fixed focus-within:left-4 focus-within:top-4 focus-within:z-[1000] focus-within:rounded-md focus-within:bg-background focus-within:px-4 focus-within:py-2 focus-within:text-foreground focus-within:shadow-lg focus-within:ring-2 focus-within:ring-ring',
  fieldsetRoot: 'min-w-0 border-0 p-0 text-foreground',
  fieldsetLegend: 'min-w-0 text-sm font-semibold leading-6 text-foreground',
  fieldsetDescription: 'mt-1 text-sm leading-5 text-muted-foreground',
  fieldsetError: 'mt-1 text-sm leading-5 text-destructive-text',
  fieldsetContent: 'mt-3 grid min-w-0 gap-3',
  stepsRoot: 'm-0 min-w-0 list-none p-0 text-foreground',
  stepsHorizontal:
    'flex w-full max-w-full items-start overflow-x-auto pb-2',
  stepsVertical: 'grid',
  stepsItem: 'relative min-w-0',
  stepsItemHorizontal: 'flex min-w-52 flex-1 items-start',
  stepsItemVertical: 'pb-6 last:pb-0',
  stepsTrigger:
    'flex min-w-0 flex-1 items-start gap-3 rounded-md border-0 bg-transparent p-1 text-start text-inherit',
  stepsTriggerInteractive:
    'cursor-pointer hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-safe:transition motion-safe:duration-300 motion-safe:ease-out',
  stepsTriggerDisabled: '!cursor-not-allowed opacity-60',
  stepsIndicator:
    'relative z-[1] inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
  stepsIndicatorPending: 'border-border bg-background text-muted-foreground',
  stepsIndicatorCurrent: 'border-primary/50 bg-primary/10 text-primary',
  stepsIndicatorCompleted:
    'border-primary bg-primary text-primary-foreground',
  stepsIndicatorError:
    'border-destructive bg-destructive text-destructive-foreground',
  stepsContent: 'grid min-w-0 gap-1 pt-0.5',
  stepsLabel: 'min-w-0 break-words text-sm font-medium leading-5',
  stepsDescription:
    'min-w-0 break-words text-sm leading-5 text-muted-foreground',
  stepsStatus: 'sr-only',
  stepsConnector: 'shrink-0 bg-border',
  stepsConnectorHorizontal: 'mt-3.5 h-px w-8',
  stepsConnectorVertical:
    'absolute bottom-0 top-8 start-3.5 w-px -translate-x-1/2',
  stepsConnectorCompleted: 'bg-primary/60',
  stepsConnectorError: 'bg-destructive/60',
  scrollAreaViewport: 'min-h-0 min-w-0',
  numberInputControl: 'tabular-nums',
  numberInputStepControls:
    'me-1 flex shrink-0 self-stretch overflow-hidden border-s border-border',
  numberInputStepButton:
    'flex min-h-6 w-7 cursor-pointer items-center justify-center border-0 bg-transparent px-1 text-xs leading-none text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:z-[1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:!cursor-not-allowed disabled:opacity-40',
  passwordInputControl: 'pe-2',
  passwordInputToggle:
    'me-1 inline-flex min-h-7 shrink-0 cursor-pointer items-center justify-center rounded px-2 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:!cursor-not-allowed disabled:opacity-40',
  inputGroupRoot:
    cn(
      'relative flex min-w-0 items-stretch overflow-hidden rounded-md border border-border bg-background text-foreground focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-ring/30',
      motionBase,
    ),
  inputGroupInvalid:
    'border-destructive/55 focus-within:border-destructive/70 focus-within:ring-destructive/25',
  inputGroupDisabled: '!cursor-not-allowed bg-muted/35',
  inputGroupContent:
    'flex min-w-0 flex-1 items-stretch [&>*]:min-w-0',
  inputGroupStartElement:
    'inline-flex shrink-0 items-center justify-center ps-3 pe-1 text-foreground/75',
  inputGroupEndElement:
    'inline-flex shrink-0 items-center justify-center ps-1 pe-3 text-foreground/75',
  inputGroupStartAddon:
    'inline-flex max-w-[min(40%,16rem)] shrink-0 items-center overflow-hidden border-e border-border bg-muted/60 px-3 text-sm text-foreground/75 text-ellipsis whitespace-nowrap [&>*]:min-w-0',
  inputGroupEndAddon:
    'inline-flex max-w-[min(40%,16rem)] shrink-0 items-center overflow-hidden border-s border-border bg-muted/60 px-3 text-sm text-foreground/75 text-ellipsis whitespace-nowrap [&>*]:min-w-0',
  panelRoot:
    cn(
      'group relative flex min-w-0 flex-col overflow-hidden rounded-lg border border-border/70 bg-background',
      motionInteractive,
    ),
  panelInteractive:
    'cursor-pointer hover:shadow-md',
  panelSelected: 'border-primary/45 bg-primary/5',
  panelDisabled: '!cursor-not-allowed opacity-60',
  panelMedia:
    'shrink-0 overflow-hidden',
  panelHeader: 'min-w-0 p-4',
  panelTitle: 'min-w-0 truncate text-sm font-semibold leading-none text-foreground',
  panelDescription: 'mt-1 text-sm leading-5 text-foreground/75',
  panelBody: 'min-w-0 px-4 pb-4 text-sm text-foreground',
  panelBodyStandalone: 'pt-4',
  panelFooter: 'shrink-0 border-t border-border/60 px-4 py-3',
  stateRoot:
    cn(
      'flex min-h-48 flex-col items-center justify-center rounded-lg border border-border/70 bg-background px-6 py-10 text-center',
      motionBase,
    ),
  stateIcon:
    'mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground motion-safe:animate-in motion-safe:fade-in',
  stateTitle: 'text-sm font-semibold text-foreground',
  stateDescription: 'mt-2 max-w-md text-sm leading-6 text-muted-foreground',
  stateActions: 'mt-5 flex flex-wrap items-center justify-center gap-2',
  loadingSpinner:
    'h-5 w-5 motion-safe:animate-spin motion-reduce:animate-none',
  fieldRoot: 'grid min-w-0 gap-2',
  fieldLabelRow: 'flex min-w-0 items-center justify-between gap-3',
  fieldLabel: 'min-w-0 text-sm font-medium leading-none text-foreground',
  fieldRequired: 'text-destructive-text',
  fieldDescription: 'text-sm leading-5 text-muted-foreground',
  fieldMessage: 'text-sm leading-5 text-muted-foreground',
  fieldControl: 'min-w-0',
  controlWrapper:
    cn(
      'relative flex min-w-0 items-center rounded-md border border-border bg-background text-foreground focus-within:border-primary/35',
      motionBase,
    ),
  controlBase:
    'min-w-0 flex-1 bg-transparent px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:!cursor-not-allowed',
  controlInput: 'h-9',
  controlTextarea: 'min-h-24 resize-y py-2 leading-6',
  controlSelect: 'h-9 cursor-pointer appearance-none pr-9 disabled:!cursor-not-allowed',
  controlIcon:
    'pointer-events-none ml-3 inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground',
  controlTrailing:
    'mr-3 inline-flex shrink-0 items-center justify-center text-muted-foreground',
  controlInvalid:
    'border-destructive/45 focus-within:border-destructive/60',
  controlDisabled: '!cursor-not-allowed opacity-60',
  controlReadOnly: 'bg-muted/35',
  selectChevron:
    cn(
      'pointer-events-none absolute right-3 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border-b border-r border-current text-muted-foreground',
      motionTransform,
    ),
  selectMenu:
    'absolute left-0 right-0 top-[calc(100%+0.375rem)] z-50 max-h-60 origin-top overflow-auto rounded-md border border-border bg-background p-1 text-sm text-foreground shadow-lg outline-none motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:ease-out',
  selectOption:
    cn(
      'flex min-h-8 w-full min-w-0 cursor-pointer items-center rounded-sm px-2.5 py-1.5 text-left text-sm text-foreground',
      motionInteractive,
    ),
  selectOptionActive: 'bg-accent text-accent-foreground',
  selectOptionSelected: 'bg-primary/10 text-primary',
  selectOptionDisabled: '!cursor-not-allowed opacity-50',
  selectPlaceholder: 'text-muted-foreground',
  selectNative: 'sr-only',
  switchRoot: 'inline-flex min-w-0 cursor-pointer items-start gap-3',
  switchInput: 'sr-only',
  switchTrack:
    cn(
      'relative mt-0.5 inline-flex h-6 w-10 shrink-0 items-center rounded-full border border-border bg-muted focus-within:border-primary/35',
      motionBase,
    ),
  switchThumb:
    cn(
      'h-4 w-4 translate-x-1 rounded-full bg-background shadow-sm',
      motionTransform,
    ),
  switchChecked: 'border-primary/40 bg-primary/80 text-primary-foreground',
  switchDisabled: '!cursor-not-allowed opacity-60',
  switchContent: 'grid min-w-0 gap-1',
  switchLabel: 'text-sm font-medium leading-none text-foreground',
  switchDescription: 'text-sm leading-5 text-muted-foreground',
  checkboxRoot: 'inline-flex min-w-0 cursor-pointer items-start gap-3',
  checkboxInput: 'sr-only',
  checkboxBox:
    cn(
      'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-background text-primary focus-within:border-primary/40',
      motionBase,
    ),
  checkboxBoxChecked: 'border-primary/45 bg-primary text-primary-foreground',
  checkboxBoxInvalid: 'border-destructive/50',
  checkboxIndicator: 'text-[11px] font-bold leading-none',
  checkboxDisabled: '!cursor-not-allowed opacity-60',
  checkboxContent: 'grid min-w-0 gap-1',
  checkboxLabel: 'text-sm font-medium leading-none text-foreground',
  checkboxDescription: 'text-sm leading-5 text-muted-foreground',
  radioGroupRoot: 'grid min-w-0 gap-2',
  radioOption:
    cn(
      'inline-flex min-w-0 cursor-pointer items-start gap-3 rounded-md border border-border/60 bg-background px-3 py-2 text-sm',
      motionInteractive,
      'hover:bg-muted/35',
    ),
  radioInput: 'sr-only',
  radioMarker:
    cn(
      'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-border bg-background text-primary focus-within:border-primary/40',
      motionBase,
    ),
  radioMarkerChecked: 'border-primary/50 bg-primary/10',
  radioIndicator: 'h-2 w-2 rounded-full bg-primary',
  radioOptionDisabled: '!cursor-not-allowed opacity-60 hover:bg-background',
  radioContent: 'grid min-w-0 gap-1',
  radioLabel: 'text-sm font-medium leading-none text-foreground',
  radioDescription: 'text-sm leading-5 text-muted-foreground',
  dialogRoot: 'relative',
  dialogTrigger: '',
  dialogTriggerDisabled: '!cursor-not-allowed opacity-60',
  dialogOverlay:
    'fixed inset-0 z-50 bg-background/70 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:ease-out',
  dialogContent:
    'pointer-events-none fixed inset-0 z-50 flex min-h-0 items-center justify-center p-4',
  dialogPanel:
    cn(
      'pointer-events-auto relative flex max-h-[min(85vh,720px)] w-full max-w-lg min-w-0 flex-col overflow-hidden rounded-lg border border-border/70 bg-background text-foreground shadow-xl',
      'motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-300 motion-safe:ease-out',
    ),
  dialogHeader: 'grid min-w-0 gap-1 border-b border-border/60 px-5 py-4',
  dialogTitle: 'min-w-0 pr-8 text-base font-semibold leading-6 text-foreground',
  dialogDescription: 'text-sm leading-6 text-muted-foreground',
  dialogBody: 'min-h-0 min-w-0 overflow-auto px-5 py-4 text-sm text-foreground',
  dialogFooter:
    'flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-border/60 px-5 py-4',
  dialogClose:
    cn(
      'absolute right-3 top-3 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
      motionInteractive,
    ),
  dropdownRoot: 'relative inline-flex min-w-0',
  dropdownTrigger: '',
  dropdownTriggerDisabled: '!cursor-not-allowed opacity-60',
  dropdownMenu:
    'z-50 min-w-48 overflow-hidden rounded-md border border-border bg-background p-1 text-sm text-foreground shadow-lg motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:ease-out',
  dropdownMenuTop: 'bottom-[calc(100%+0.375rem)] top-auto',
  dropdownMenuEnd: 'left-auto right-0',
  dropdownLabel:
    'px-2.5 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground',
  dropdownSeparator: 'my-1 h-px bg-border/70',
  dropdownItem:
    cn(
      'flex min-w-0 cursor-pointer items-center gap-2 rounded-sm px-2.5 py-2 text-left text-sm text-foreground hover:bg-accent/60 focus:bg-accent/60 data-[highlighted]:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
      motionInteractive,
    ),
  dropdownItemDanger:
    'text-destructive-text hover:bg-destructive/10 focus:bg-destructive/10 data-[highlighted]:bg-destructive/10',
  dropdownItemDisabled:
    '!cursor-not-allowed opacity-50 hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent',
  dropdownItemIcon: 'inline-flex h-4 w-4 shrink-0 items-center justify-center',
  dropdownItemContent: 'grid min-w-0 flex-1 gap-0.5',
  dropdownItemLabel: 'min-w-0 truncate',
  dropdownItemDescription:
    'min-w-0 truncate text-xs leading-4 text-muted-foreground',
  dropdownItemShortcut:
    'ml-3 shrink-0 text-xs font-medium text-muted-foreground',
  tooltipRoot: 'relative inline-flex min-w-0',
  tooltipTrigger: 'inline-flex min-w-0',
  tooltipContent:
    'pointer-events-none z-50 max-w-xs rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs leading-5 text-popover-foreground shadow-lg motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:ease-out',
  noticeBase:
    cn(
      'flex min-w-0 gap-3 rounded-lg border px-4 py-3 text-sm',
      motionBase,
      'motion-safe:animate-in motion-safe:fade-in',
    ),
  noticeDefault: 'border-border bg-background text-foreground',
  noticePrimary: 'border-primary/25 bg-primary/10 text-primary',
  noticeSuccess:
    'border-[hsl(var(--success,142_72%_29%)_/_0.30)] bg-[hsl(var(--success,142_72%_29%)_/_0.10)] text-foreground',
  noticeWarning: 'border-[hsl(var(--warning,45_93%_47%)_/_0.30)] bg-[hsl(var(--warning,45_93%_47%)_/_0.10)] text-[hsl(var(--warning-foreground,28_73%_26%))]',
  noticeDanger:
    'border-destructive/30 bg-destructive/10 text-destructive-text',
  noticeMuted: 'border-border/60 bg-muted/35 text-muted-foreground',
  noticeIcon:
    'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center motion-safe:animate-in motion-safe:fade-in',
  noticeTitle: 'font-semibold leading-5',
  noticeDescription: 'leading-6',
  noticeActions: 'mt-3 flex flex-wrap items-center gap-2',
  dividerRoot: cn('shrink-0 border-border/70', motionBase),
  dividerLabel:
    'mx-3 shrink-0 text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground motion-safe:animate-in motion-safe:fade-in',
  sectionHeaderRoot:
    'flex min-w-0 flex-wrap items-start justify-between gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:ease-out',
  sectionHeaderContent: 'grid min-w-0 gap-1',
  sectionHeaderEyebrow:
    'text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground',
  sectionHeaderTitle: 'text-base font-semibold leading-6 text-foreground',
  sectionHeaderDescription: 'max-w-2xl text-sm leading-6 text-muted-foreground',
  sectionHeaderActions: 'flex shrink-0 flex-wrap items-center gap-2',
  tabsRoot: 'grid min-w-0 gap-3',
  tabsList:
    cn(
      'inline-flex min-w-0 flex-wrap items-center gap-1 rounded-lg border border-border/70 bg-muted/30 p-1',
      motionBase,
    ),
  tabsTrigger:
    cn(
      'inline-flex min-w-0 cursor-pointer items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-background/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1 motion-reduce:transition-none',
      motionInteractive,
    ),
  tabsTriggerActive:
    'bg-background text-foreground shadow-sm',
  tabsTriggerDisabled: '!cursor-not-allowed opacity-50 hover:bg-transparent',
  tabsPanel:
    'min-w-0 rounded-lg border border-border/70 bg-background p-4 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none',
  segmentedRoot:
    cn(
      'inline-flex min-w-0 items-center gap-1 rounded-lg border border-border/70 bg-muted/30 p-1',
      motionBase,
    ),
  segmentedItem:
    cn(
      'inline-flex min-w-0 cursor-pointer items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-background/70 hover:text-foreground focus-visible:outline-none',
      motionInteractive,
    ),
  segmentedItemActive:
    'bg-background text-foreground shadow-sm',
  segmentedItemDisabled: '!cursor-not-allowed opacity-50 hover:bg-transparent',
  progressRoot: 'grid min-w-0 gap-2',
  progressTrack:
    'h-2 w-full overflow-hidden rounded-full bg-muted',
  progressIndicator:
    'h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-500 motion-safe:ease-out',
  progressLabelRow: 'flex min-w-0 items-center justify-between gap-3',
  progressLabel: 'min-w-0 truncate text-sm font-medium text-foreground',
  progressValue: 'shrink-0 text-xs font-medium text-muted-foreground',
  skeletonRoot:
    'relative overflow-hidden rounded-md bg-muted/70',
  avatarRoot:
    'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-sm font-semibold text-muted-foreground motion-safe:transition motion-safe:duration-300 motion-safe:ease-out',
  avatarImage:
    'h-full w-full object-cover',
  avatarFallback:
    'flex h-full w-full items-center justify-center bg-muted text-foreground',
  avatarStatus:
    'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-background bg-muted-foreground motion-safe:animate-pulse',
  avatarText: 'min-w-0 truncate text-sm font-medium text-foreground',
  toolbarRoot:
    cn(
      'flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-border/70 bg-background px-4 py-3',
      motionBase,
    ),
  toolbarLeading: 'flex min-w-0 items-center gap-3',
  toolbarTitle: 'min-w-0 truncate text-sm font-semibold text-foreground',
  toolbarDescription: 'mt-0.5 text-sm leading-5 text-muted-foreground',
  toolbarContent: 'min-w-0 flex-1',
  toolbarActions: 'flex shrink-0 flex-wrap items-center gap-2',
  breadcrumbRoot: 'min-w-0 text-sm text-muted-foreground',
  breadcrumbList: 'flex min-w-0 flex-wrap items-center gap-1.5',
  breadcrumbItem: 'inline-flex min-w-0 items-center gap-1.5',
  breadcrumbLink:
    'min-w-0 cursor-pointer truncate text-muted-foreground hover:text-foreground disabled:!cursor-not-allowed disabled:opacity-50 aria-disabled:!cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:text-muted-foreground motion-safe:transition motion-safe:duration-300 motion-safe:ease-out',
  breadcrumbCurrent: 'min-w-0 truncate font-medium text-foreground',
  breadcrumbSeparator: 'shrink-0 text-muted-foreground/70',
  paginationRoot:
    'flex min-w-0 flex-wrap items-center justify-between gap-3 text-sm',
  paginationInfo: 'min-w-0 text-muted-foreground',
  paginationControls: 'flex min-w-0 flex-wrap items-center justify-end gap-1.5',
  paginationButton:
    'inline-flex h-8 min-w-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-border bg-background px-2.5 text-sm font-medium text-foreground hover:bg-accent/60 disabled:!cursor-not-allowed disabled:opacity-50 motion-safe:transition motion-safe:duration-300 motion-safe:ease-out',
  paginationButtonDisabled: '!cursor-not-allowed opacity-50',
  paginationPage:
    'inline-flex h-8 min-w-8 cursor-default items-center justify-center rounded-md bg-muted/50 px-2.5 text-sm font-medium text-foreground hover:bg-muted/50',
  paginationEllipsis:
    'inline-flex h-8 min-w-6 items-center justify-center px-1 text-sm text-muted-foreground',
  descriptionListRoot:
    'grid min-w-0 gap-3 text-sm',
  descriptionListItem:
    'grid min-w-0 gap-1 rounded-md border border-border/60 bg-background px-3 py-2 motion-safe:transition motion-safe:duration-300 motion-safe:ease-out hover:shadow-sm',
  descriptionListTerm:
    'min-w-0 truncate text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground',
  descriptionListValue: 'min-w-0 text-sm leading-6 text-foreground',
  accordionRoot: 'grid min-w-0 gap-2',
  accordionItem:
    'overflow-hidden rounded-lg border border-border/70 bg-background motion-safe:transition motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none',
  accordionTrigger:
    'flex w-full min-w-0 cursor-pointer items-center justify-between gap-3 px-4 py-3 text-start text-sm font-medium text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:!cursor-not-allowed disabled:opacity-50 motion-safe:transition motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none',
  accordionTriggerIcon:
    cn(
      'h-2 w-2 shrink-0 rotate-45 border-b border-r border-current text-muted-foreground',
      motionTransform,
      'motion-reduce:transition-none',
    ),
  accordionPanel:
    'border-t border-border/60 px-4 py-3 text-sm leading-6 text-muted-foreground motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:ease-out motion-reduce:animate-none',
  menuTrigger: 'inline-flex min-w-0',
  menuContent:
    'z-50 min-w-44 overflow-hidden rounded-md border border-border bg-background p-1 text-sm text-foreground shadow-lg outline-none motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-300 motion-safe:ease-out',
  menuLabel: 'px-2 py-1.5 text-xs font-medium text-muted-foreground',
  menuSeparator: '-mx-1 my-1 h-px bg-border',
  menuItem:
    cn(
      'relative flex min-h-8 min-w-0 cursor-pointer select-none items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm text-foreground outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:!cursor-not-allowed data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
      motionInteractive,
    ),
  menuItemDefault: 'text-foreground',
  menuItemPrimary: 'text-primary focus:text-primary data-[highlighted]:text-primary',
  menuItemSuccess:
    'text-[hsl(var(--success,142_72%_29%))] focus:text-[hsl(var(--success,142_72%_29%))] data-[highlighted]:text-[hsl(var(--success,142_72%_29%))]',
  menuItemWarning:
    'text-[hsl(var(--warning-foreground,28_73%_26%))] focus:text-[hsl(var(--warning-foreground,28_73%_26%))] data-[highlighted]:text-[hsl(var(--warning-foreground,28_73%_26%))]',
  menuItemDanger:
    'text-destructive-text focus:text-destructive-text data-[highlighted]:text-destructive-text',
  menuItemMuted:
    'text-muted-foreground focus:text-foreground data-[highlighted]:text-foreground',
  menuItemDisabled:
    '!cursor-not-allowed opacity-50 data-[disabled]:pointer-events-none',
  menuItemIcon: 'inline-flex h-4 w-4 shrink-0 items-center justify-center',
  menuItemLabel: 'min-w-0 flex-1 truncate',
  menuItemShortcut:
    'ml-4 shrink-0 text-xs font-medium text-muted-foreground',
  menuCheckboxIndicator:
    'absolute left-2 inline-flex h-4 w-4 items-center justify-center text-current',
  menuRadioIndicator:
    'absolute left-2 inline-flex h-4 w-4 items-center justify-center text-current',
  menuSubTrigger:
    cn(
      'relative flex min-h-8 min-w-0 cursor-pointer select-none items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm text-foreground outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:!cursor-not-allowed data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
      motionInteractive,
    ),
  menuSubTriggerIcon:
    'ml-auto inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground',
  menuSubContent:
    'z-50 min-w-40 overflow-hidden rounded-md border border-border bg-background p-1 text-sm text-foreground shadow-lg outline-none motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-300 motion-safe:ease-out',
  tableContainer:
    cn(
      'relative isolate min-w-0 overflow-auto rounded-lg border border-border/70 bg-background/95 bg-clip-padding [scrollbar-gutter:stable]',
      '[--base-table-header-height:2.75rem] [--base-table-split-scrollbar-gutter:0.75rem]',
      '[--base-table-sticky-header-bg:hsl(var(--background))] [--base-table-header-separator:hsl(var(--border)_/_0.7)]',
      tableScrollbarChrome,
      motionBase,
    ),
  tableContainerFooterAttached: '!rounded-b-none !shadow-none',
  tableRoot: 'w-full caption-bottom border-separate border-spacing-0 text-sm text-foreground',
  tableCaption: 'mt-3 text-sm text-muted-foreground',
  tableHeader:
    cn(
      'relative z-[2] bg-transparent [&_tr]:bg-transparent',
      tableStickyHeaderCells,
    ),
  tableBody: '[&_tr:last-child>*]:border-b-0',
  tableFooter: 'bg-muted/30 font-medium [&_tr>*]:border-t [&_tr>*]:border-border/70',
  tableFooterBar:
    cn(
      'flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-lg border border-border/70 bg-muted/20 px-4 py-3 text-sm text-foreground shadow-none',
      motionBase,
    ),
  tableFooterBarAttached:
    '-mt-px rounded-t-none border-t border-border/70 shadow-none',
  tableFooterBarLeading: 'flex min-w-0 items-center gap-2',
  tableFooterBarContent: 'min-w-0 flex-1',
  tableFooterBarActions: 'ms-auto flex shrink-0 flex-wrap items-center gap-2',
  tableSplitRoot:
    'grid w-full min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)_auto] caption-bottom border-separate border-spacing-0 text-sm text-foreground',
  tableSplitHeader:
    'block w-full min-w-0 shrink-0 overflow-x-hidden overflow-y-hidden bg-transparent',
  tableSplitBody:
    cn(
      'block min-h-0 w-full min-w-0 max-w-full justify-self-stretch overflow-auto [scrollbar-gutter:stable]',
      tableScrollbarChrome,
    ),
  tableSplitFooter:
    'block w-full min-w-0 shrink-0 overflow-x-hidden overflow-y-hidden',
  tableSplitRow:
    'grid w-max min-w-full [grid-template-columns:var(--base-table-column-template)]',
  tableSplitHead: 'flex min-w-0 overflow-hidden',
  tableSplitCell: 'block min-w-0 overflow-hidden [overflow-wrap:anywhere]',
  tableRow:
    cn(
      '[&>*]:border-b [&>*]:border-border/60 data-[state=selected]:bg-primary/5 data-[state=selected]:[&>*]:bg-primary/5',
      motionBase,
    ),
  tableRowInteractive:
    'cursor-pointer [--base-table-row-hover-bg:hsl(var(--muted)_/_0.35)] hover:bg-[var(--base-table-row-hover-bg)] hover:[&>*]:bg-[var(--base-table-row-hover-bg)]',
  tableRowSelected: 'bg-primary/5 [&>*]:bg-primary/5',
  tableRowDisabled: '!cursor-not-allowed opacity-60',
  tableHead:
    'relative h-11 whitespace-nowrap px-3 py-2 text-left align-middle text-xs font-semibold text-muted-foreground shadow-[inset_0_-1px_0_var(--base-table-header-separator)]',
  tableHeadContent: 'flex min-w-0 items-center justify-between gap-2',
  tableHeadMain: 'flex min-w-0 items-center gap-2',
  tableHeadIcon: 'inline-flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground',
  tableHeadText: 'grid min-w-0 gap-0.5',
  tableHeadLabel: 'min-w-0 truncate text-xs font-semibold leading-4 text-foreground',
  tableHeadDescription: 'min-w-0 truncate text-[11px] font-normal leading-4 text-muted-foreground',
  tableHeadMeta: 'shrink-0 text-[11px] font-medium text-muted-foreground',
  tableHeadActions: 'flex shrink-0 items-center gap-1',
  tableHeadSortButton:
    cn(
      'inline-flex min-w-0 cursor-pointer items-center gap-1 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:!cursor-not-allowed disabled:opacity-60',
      motionBase,
    ),
  tableHeadSortIcon:
    cn(
      'inline-flex h-3 w-3 shrink-0 items-center justify-center text-muted-foreground',
      motionTransform,
    ),
  tableHeadDragHandle:
    cn(
      'inline-flex h-5 w-4 shrink-0 cursor-grab items-center justify-center rounded-sm text-muted-foreground hover:bg-muted active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:!cursor-not-allowed disabled:opacity-50',
      motionBase,
    ),
  tableHeadResizeHandle:
    cn(
      'absolute end-0 top-1/2 h-7 w-2 -translate-y-1/2 cursor-col-resize touch-none rounded-sm after:absolute after:start-1/2 after:top-1/2 after:h-5 after:w-px after:-translate-x-1/2 after:-translate-y-1/2 after:bg-border hover:after:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:!cursor-not-allowed disabled:opacity-50',
      motionBase,
    ),
  tableCell: 'min-w-0 px-3 py-2 align-middle text-sm text-foreground [overflow-wrap:anywhere]',
  tableCellAlignCenter: 'text-center',
  tableCellAlignRight: 'text-right',
  tableEmptyRow: '[&>*]:border-b [&>*]:border-border/60',
  tableEmptyCell: 'px-4 py-10 text-center',
  tableEmptyContent: 'mx-auto flex max-w-md flex-col items-center justify-center text-center',
  tableEmptyIcon:
    'mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground',
  tableEmptyTitle: 'text-sm font-semibold text-foreground',
  tableEmptyDescription: 'mt-1 text-sm leading-6 text-muted-foreground',
  tableEmptyActions: 'mt-4 flex flex-wrap items-center justify-center gap-2',
  tableLoadingRow: '[&>*]:border-b [&>*]:border-border/60',
  tableLoadingCell: 'px-3 py-2',
  tableLoadingSkeleton: 'h-4 w-full rounded-md bg-muted/70',
  layoutRoot:
    cn(
      'grid min-w-0 overflow-hidden grid-rows-[auto_minmax(0,1fr)_auto] gap-3 rounded-lg border border-border/70 bg-background p-3 text-foreground',
      motionBase,
    ),
  layoutRootFull: 'h-full min-h-0',
  layoutHeader: 'min-w-0',
  layoutBody: 'grid min-h-0 min-w-0 overflow-hidden gap-3',
  layoutSidebar: 'min-h-0 min-w-0 overflow-hidden',
  layoutMain: 'min-h-0 min-w-0 overflow-hidden',
  layoutAside: 'min-h-0 min-w-0 overflow-hidden',
  layoutFooter: 'min-w-0',
  layoutPane:
    cn(
      'h-full max-h-full min-h-0 min-w-0 overflow-hidden rounded-lg bg-background text-foreground',
      motionBase,
    ),
  layoutPaneScrollable: 'overflow-auto',
  layoutPaneBordered: 'border border-border/70',
  layoutPaneMuted: 'bg-muted/25',
  layoutPaneSoft: 'bg-background/95 shadow-sm',
  layoutPaneTransparent: 'bg-transparent shadow-none',
  layoutSplitRoot: 'grid min-h-0 min-w-0 overflow-hidden',
  layoutSplitHorizontal: 'grid-flow-col',
  layoutSplitVertical: 'grid-flow-row',
  layoutSplitPane: 'min-h-0 min-w-0 overflow-hidden',
  layoutSplitPrimary: '',
  layoutSplitSecondary: '',
  layoutResizeHandle:
    cn(
      'group/resize relative z-10 flex shrink-0 items-center justify-center bg-transparent text-muted-foreground hover:bg-border/60 active:bg-border/70 focus-visible:bg-border/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
      motionInteractive,
    ),
  layoutResizeHandleHorizontal: 'w-2 cursor-col-resize',
  layoutResizeHandleVertical: 'h-2 cursor-row-resize',
  layoutResizeHandleDisabled: 'cursor-default opacity-60',
  layoutResizeHandleIndicator:
    'rounded-full bg-muted-foreground/45 group-hover/resize:bg-primary/70',
};

const densitySlots: Record<BaseDensity, Partial<BaseThemeSlots>> = {
  compact: {
    buttonBase: 'h-8 px-2.5 text-xs',
    buttonIconOnly: 'h-8 w-8 px-0',
    badgeBase: 'px-1.5 py-0 text-[11px]',
    panelHeader: 'p-3',
    panelBody: 'px-3 pb-3 text-xs',
    panelBodyStandalone: 'pt-3',
    panelFooter: 'px-3 py-2',
    stateRoot: 'min-h-36 px-4 py-7',
    stateIcon: 'mb-3 h-10 w-10',
    fieldRoot: 'gap-1.5',
    fieldLabel: 'text-xs',
    fieldDescription: 'text-xs leading-5',
    fieldMessage: 'text-xs leading-5',
    controlInput: 'h-8 text-xs',
    controlTextarea: 'min-h-20 text-xs',
    controlSelect: 'h-8 text-xs',
    selectMenu: 'text-xs',
    selectOption: 'min-h-7 px-2 py-1 text-xs',
    switchTrack: 'h-5 w-9',
    switchThumb: 'h-3.5 w-3.5',
    checkboxBox: 'h-3.5 w-3.5',
    checkboxLabel: 'text-xs',
    checkboxDescription: 'text-xs leading-5',
    radioOption: 'px-2 py-1.5 text-xs',
    radioMarker: 'h-3.5 w-3.5',
    radioIndicator: 'h-1.5 w-1.5',
    radioLabel: 'text-xs',
    radioDescription: 'text-xs leading-5',
    dialogPanel: 'max-w-md',
    dialogHeader: 'px-4 py-3',
    dialogTitle: 'text-sm leading-5',
    dialogDescription: 'text-xs leading-5',
    dialogBody: 'px-4 py-3 text-xs',
    dialogFooter: 'px-4 py-3',
    dropdownMenu: 'min-w-44 text-xs',
    dropdownItem: 'px-2 py-1.5 text-xs',
    tooltipContent: 'text-[11px] leading-4',
    noticeBase: 'px-3 py-2 text-xs',
    dividerLabel: 'text-[11px]',
    sectionHeaderRoot: 'gap-2',
    sectionHeaderTitle: 'text-sm leading-5',
    sectionHeaderDescription: 'text-xs leading-5',
    tabsList: 'gap-0.5 p-0.5',
    tabsTrigger: 'px-2 py-1 text-xs',
    tabsPanel: 'p-3 text-xs',
    segmentedRoot: 'gap-0.5 p-0.5',
    segmentedItem: 'px-2 py-1 text-xs',
    progressTrack: 'h-1.5',
    avatarRoot: 'text-xs',
    toolbarRoot: 'gap-2 px-3 py-2',
    toolbarTitle: 'text-xs',
    toolbarDescription: 'text-xs leading-5',
    paginationButton: 'h-7 min-w-7 px-2 text-xs',
    paginationPage: 'h-7 min-w-7 px-2 text-xs',
    descriptionListRoot: 'gap-2 text-xs',
    descriptionListItem: 'px-2 py-1.5',
    descriptionListTerm: 'text-[11px]',
    descriptionListValue: 'text-xs leading-5',
    accordionTrigger: 'px-3 py-2 text-xs',
    accordionPanel: 'px-3 py-2 text-xs leading-5',
    menuContent: 'min-w-40 p-0.5 text-xs',
    menuSubContent: 'min-w-36 p-0.5 text-xs',
    menuLabel: 'px-2 py-1 text-[11px]',
    menuItem: 'min-h-7 px-2 py-1 text-xs',
    menuSubTrigger: 'min-h-7 px-2 py-1 text-xs',
    menuItemShortcut: 'text-[11px]',
    tableContainer: '[--base-table-header-height:2.25rem]',
    tableHead: 'h-9 px-2 py-1.5 text-[11px]',
    tableCell: 'px-2 py-1.5 text-xs',
    tableHeadLabel: 'text-[11px]',
    tableHeadDescription: 'text-[10px] leading-3',
    tableLoadingCell: 'px-2 py-1.5',
    layoutRoot: 'gap-2 p-2',
    layoutBody: 'gap-2',
    layoutPane: 'rounded-md text-xs',
    layoutResizeHandleHorizontal: 'w-1.5',
    layoutResizeHandleVertical: 'h-1.5',
  },
  default: {
    buttonBase: 'h-9',
  },
  comfortable: {
    buttonBase: 'h-10 px-3.5',
    buttonIconOnly: 'h-10 w-10 px-0',
    badgeBase: 'px-2.5 py-1',
    panelHeader: 'p-5',
    panelBody: 'px-5 pb-5',
    panelBodyStandalone: 'pt-5',
    panelFooter: 'px-5 py-3.5',
    stateRoot: 'min-h-56 px-8 py-12',
    stateIcon: 'mb-5 h-14 w-14',
    fieldRoot: 'gap-2.5',
    controlInput: 'h-10',
    controlTextarea: 'min-h-28',
    controlSelect: 'h-10',
    selectMenu: 'p-1.5',
    selectOption: 'min-h-9 px-3 py-2',
    switchTrack: 'h-7 w-12',
    switchThumb: 'h-5 w-5',
    checkboxBox: 'h-5 w-5',
    radioOption: 'px-4 py-3',
    radioMarker: 'h-5 w-5',
    radioIndicator: 'h-2.5 w-2.5',
    dialogPanel: 'max-w-xl',
    dialogHeader: 'px-6 py-5',
    dialogBody: 'px-6 py-5',
    dialogFooter: 'px-6 py-5',
    dropdownMenu: 'min-w-56',
    dropdownItem: 'px-3 py-2.5',
    noticeBase: 'px-5 py-4',
    sectionHeaderTitle: 'text-lg leading-7',
    tabsTrigger: 'px-3.5 py-2',
    tabsPanel: 'p-5',
    segmentedItem: 'px-3.5 py-2',
    progressTrack: 'h-2.5',
    toolbarRoot: 'px-5 py-4',
    paginationButton: 'h-9 min-w-9 px-3',
    paginationPage: 'h-9 min-w-9 px-3',
    descriptionListItem: 'px-4 py-3',
    accordionTrigger: 'px-5 py-4',
    accordionPanel: 'px-5 py-4',
    menuContent: 'min-w-48 p-1.5',
    menuSubContent: 'min-w-44 p-1.5',
    menuItem: 'min-h-9 px-3 py-2',
    menuSubTrigger: 'min-h-9 px-3 py-2',
    tableContainer: '[--base-table-header-height:3rem]',
    tableHead: 'h-12 px-4 py-3',
    tableCell: 'px-4 py-3',
    tableLoadingCell: 'px-4 py-3',
    layoutRoot: 'gap-4 p-4',
    layoutBody: 'gap-4',
    layoutPane: 'rounded-xl',
    layoutResizeHandleHorizontal: 'w-2.5',
    layoutResizeHandleVertical: 'h-2.5',
  },
};

const radiusSlots: Record<BaseRadius, Partial<BaseThemeSlots>> = {
  none: {
    buttonBase: 'rounded-none',
    badgeBase: 'rounded-none',
    panelRoot: 'rounded-none',
    stateRoot: 'rounded-none',
    stateIcon: 'rounded-none',
    controlWrapper: 'rounded-none',
    selectMenu: 'rounded-none',
    selectOption: 'rounded-none',
    switchTrack: 'rounded-none',
    switchThumb: 'rounded-none',
    checkboxBox: 'rounded-none',
    radioOption: 'rounded-none',
    dialogPanel: 'rounded-none',
    dialogClose: 'rounded-none',
    dropdownMenu: 'rounded-none',
    dropdownItem: 'rounded-none',
    tooltipContent: 'rounded-none',
    noticeBase: 'rounded-none',
    tabsList: 'rounded-none',
    tabsTrigger: 'rounded-none',
    tabsPanel: 'rounded-none',
    segmentedRoot: 'rounded-none',
    segmentedItem: 'rounded-none',
    progressTrack: 'rounded-none',
    progressIndicator: 'rounded-none',
    skeletonRoot: 'rounded-none',
    avatarRoot: 'rounded-none',
    avatarStatus: 'rounded-none',
    toolbarRoot: 'rounded-none',
    paginationButton: 'rounded-none',
    paginationPage: 'rounded-none',
    descriptionListItem: 'rounded-none',
    accordionItem: 'rounded-none',
    menuContent: 'rounded-none',
    menuItem: 'rounded-none',
    menuSubTrigger: 'rounded-none',
    menuSubContent: 'rounded-none',
    tableContainer: 'rounded-none',
    tableEmptyIcon: 'rounded-none',
    tableLoadingSkeleton: 'rounded-none',
    layoutRoot: 'rounded-none',
    layoutPane: 'rounded-none',
  },
  sm: {
    buttonBase: 'rounded-sm',
    badgeBase: 'rounded-sm',
    panelRoot: 'rounded-sm',
    stateRoot: 'rounded-sm',
    stateIcon: 'rounded-sm',
    controlWrapper: 'rounded-sm',
    selectMenu: 'rounded-sm',
    selectOption: 'rounded-sm',
    switchTrack: 'rounded-sm',
    switchThumb: 'rounded-sm',
    checkboxBox: 'rounded-sm',
    radioOption: 'rounded-sm',
    dialogPanel: 'rounded-sm',
    dialogClose: 'rounded-sm',
    dropdownMenu: 'rounded-sm',
    dropdownItem: 'rounded-sm',
    tooltipContent: 'rounded-sm',
    noticeBase: 'rounded-sm',
    tabsList: 'rounded-sm',
    tabsTrigger: 'rounded-sm',
    tabsPanel: 'rounded-sm',
    segmentedRoot: 'rounded-sm',
    segmentedItem: 'rounded-sm',
    progressTrack: 'rounded-sm',
    progressIndicator: 'rounded-sm',
    skeletonRoot: 'rounded-sm',
    avatarRoot: 'rounded-sm',
    avatarStatus: 'rounded-sm',
    toolbarRoot: 'rounded-sm',
    paginationButton: 'rounded-sm',
    paginationPage: 'rounded-sm',
    descriptionListItem: 'rounded-sm',
    accordionItem: 'rounded-sm',
    menuContent: 'rounded-sm',
    menuItem: 'rounded-sm',
    menuSubTrigger: 'rounded-sm',
    menuSubContent: 'rounded-sm',
    tableContainer: 'rounded-sm',
    tableEmptyIcon: 'rounded-sm',
    tableLoadingSkeleton: 'rounded-sm',
    layoutRoot: 'rounded-sm',
    layoutPane: 'rounded-sm',
  },
  md: {},
  lg: {
    buttonBase: 'rounded-lg',
    badgeBase: 'rounded-lg',
    panelRoot: 'rounded-xl',
    stateRoot: 'rounded-xl',
    stateIcon: 'rounded-xl',
    controlWrapper: 'rounded-lg',
    selectMenu: 'rounded-lg',
    selectOption: 'rounded-md',
    switchTrack: 'rounded-full',
    switchThumb: 'rounded-full',
    checkboxBox: 'rounded-md',
    radioOption: 'rounded-xl',
    dialogPanel: 'rounded-xl',
    dialogClose: 'rounded-lg',
    dropdownMenu: 'rounded-lg',
    dropdownItem: 'rounded-md',
    tooltipContent: 'rounded-md',
    noticeBase: 'rounded-xl',
    tabsList: 'rounded-xl',
    tabsTrigger: 'rounded-lg',
    tabsPanel: 'rounded-xl',
    segmentedRoot: 'rounded-xl',
    segmentedItem: 'rounded-lg',
    skeletonRoot: 'rounded-lg',
    toolbarRoot: 'rounded-xl',
    descriptionListItem: 'rounded-lg',
    accordionItem: 'rounded-xl',
    menuContent: 'rounded-lg',
    menuItem: 'rounded-md',
    menuSubTrigger: 'rounded-md',
    menuSubContent: 'rounded-lg',
    tableContainer: 'rounded-xl',
    tableEmptyIcon: 'rounded-xl',
    tableLoadingSkeleton: 'rounded-lg',
    layoutRoot: 'rounded-xl',
    layoutPane: 'rounded-xl',
  },
  xl: {
    buttonBase: 'rounded-xl',
    badgeBase: 'rounded-xl',
    panelRoot: 'rounded-2xl',
    stateRoot: 'rounded-2xl',
    stateIcon: 'rounded-2xl',
    controlWrapper: 'rounded-xl',
    selectMenu: 'rounded-xl',
    selectOption: 'rounded-lg',
    switchTrack: 'rounded-full',
    switchThumb: 'rounded-full',
    checkboxBox: 'rounded-lg',
    radioOption: 'rounded-2xl',
    dialogPanel: 'rounded-2xl',
    dialogClose: 'rounded-xl',
    dropdownMenu: 'rounded-xl',
    dropdownItem: 'rounded-lg',
    tooltipContent: 'rounded-lg',
    noticeBase: 'rounded-2xl',
    tabsList: 'rounded-2xl',
    tabsTrigger: 'rounded-xl',
    tabsPanel: 'rounded-2xl',
    segmentedRoot: 'rounded-2xl',
    segmentedItem: 'rounded-xl',
    skeletonRoot: 'rounded-xl',
    toolbarRoot: 'rounded-2xl',
    paginationButton: 'rounded-xl',
    paginationPage: 'rounded-xl',
    descriptionListItem: 'rounded-xl',
    accordionItem: 'rounded-2xl',
    menuContent: 'rounded-xl',
    menuItem: 'rounded-lg',
    menuSubTrigger: 'rounded-lg',
    menuSubContent: 'rounded-xl',
    tableContainer: 'rounded-2xl',
    tableEmptyIcon: 'rounded-2xl',
    tableLoadingSkeleton: 'rounded-xl',
    layoutRoot: 'rounded-2xl',
    layoutPane: 'rounded-2xl',
  },
  full: {
    buttonBase: 'rounded-full',
    badgeBase: 'rounded-full',
    panelRoot: 'rounded-2xl',
    stateRoot: 'rounded-2xl',
    stateIcon: 'rounded-full',
    controlWrapper: 'rounded-full',
    selectMenu: 'rounded-2xl',
    selectOption: 'rounded-full',
    switchTrack: 'rounded-full',
    switchThumb: 'rounded-full',
    checkboxBox: 'rounded-full',
    radioOption: 'rounded-2xl',
    dialogPanel: 'rounded-2xl',
    dialogClose: 'rounded-full',
    dropdownMenu: 'rounded-2xl',
    dropdownItem: 'rounded-full',
    tooltipContent: 'rounded-full',
    noticeBase: 'rounded-2xl',
    tabsList: 'rounded-full',
    tabsTrigger: 'rounded-full',
    tabsPanel: 'rounded-2xl',
    segmentedRoot: 'rounded-full',
    segmentedItem: 'rounded-full',
    progressTrack: 'rounded-full',
    progressIndicator: 'rounded-full',
    skeletonRoot: 'rounded-full',
    avatarRoot: 'rounded-full',
    avatarStatus: 'rounded-full',
    toolbarRoot: 'rounded-2xl',
    paginationButton: 'rounded-full',
    paginationPage: 'rounded-full',
    descriptionListItem: 'rounded-2xl',
    accordionItem: 'rounded-2xl',
    menuContent: 'rounded-2xl',
    menuItem: 'rounded-full',
    menuSubTrigger: 'rounded-full',
    menuSubContent: 'rounded-2xl',
    tableContainer: 'rounded-2xl',
    tableEmptyIcon: 'rounded-full',
    tableLoadingSkeleton: 'rounded-full',
    layoutRoot: 'rounded-2xl',
    layoutPane: 'rounded-2xl',
  },
};

const borderSlots: Record<BaseBorder, Partial<BaseThemeSlots>> = {
  none: {
    buttonBase: 'border-transparent',
    badgeBase: 'border-transparent',
    panelRoot: 'border-transparent',
    panelFooter: 'border-transparent',
    stateRoot: 'border-transparent',
    controlWrapper: 'border-transparent',
    selectMenu: 'border-transparent',
    switchTrack: 'border-transparent',
    checkboxBox: 'border-transparent',
    radioOption: 'border-transparent',
    radioMarker: 'border-transparent',
    dialogPanel: 'border-transparent',
    dialogHeader: 'border-transparent',
    dialogFooter: 'border-transparent',
    dropdownMenu: 'border-transparent',
    tooltipContent: 'border-transparent',
    noticeBase: 'border-transparent',
    dividerRoot: 'border-transparent',
    tabsList: 'border-transparent',
    tabsPanel: 'border-transparent',
    segmentedRoot: 'border-transparent',
    avatarRoot: 'border-transparent',
    toolbarRoot: 'border-transparent',
    paginationButton: 'border-transparent',
    descriptionListItem: 'border-transparent',
    accordionItem: 'border-transparent',
    accordionPanel: 'border-transparent',
    menuContent: 'border-transparent',
    menuSeparator: 'bg-transparent',
    menuSubContent: 'border-transparent',
    tableContainer: 'border-transparent [--base-table-header-separator:transparent]',
    tableHeader: 'border-transparent',
    tableFooter: '[&_tr>*]:border-transparent',
    tableFooterBar: 'border-transparent',
    tableFooterBarAttached: 'border-t border-transparent',
    tableRow: '[&>*]:border-transparent',
    tableEmptyRow: '[&>*]:border-transparent',
    tableLoadingRow: '[&>*]:border-transparent',
    layoutRoot: 'border-transparent',
    layoutPaneBordered: 'border-transparent',
    layoutResizeHandle: 'bg-transparent hover:bg-transparent active:bg-transparent focus-visible:bg-transparent',
  },
  subtle: {
    buttonDefault: 'border-border/60',
    badgeDefault: 'border-border/60',
    panelRoot: 'border-border/60',
    panelFooter: 'border-border/60',
    stateRoot: 'border-border/60',
    controlWrapper: 'border-border/60',
    selectMenu: 'border-border/60',
    switchTrack: 'border-border/60',
    checkboxBox: 'border-border/60',
    radioOption: 'border-border/60',
    radioMarker: 'border-border/60',
    dialogPanel: 'border-border/60',
    dialogHeader: 'border-border/60',
    dialogFooter: 'border-border/60',
    dropdownMenu: 'border-border/60',
    tooltipContent: 'border-border/60',
    noticeDefault: 'border-border/60',
    dividerRoot: 'border-border/60',
    tabsList: 'border-border/60',
    tabsPanel: 'border-border/60',
    segmentedRoot: 'border-border/60',
    avatarRoot: 'border-border/60',
    toolbarRoot: 'border-border/60',
    paginationButton: 'border-border/60',
    descriptionListItem: 'border-border/60',
    accordionItem: 'border-border/60',
    accordionPanel: 'border-border/60',
    menuContent: 'border-border/60',
    menuSeparator: 'bg-border/60',
    menuSubContent: 'border-border/60',
    tableContainer: 'border-border/60 [--base-table-header-separator:hsl(var(--border)_/_0.6)]',
    tableHeader: 'border-border/60',
    tableFooter: '[&_tr>*]:border-border/60',
    tableFooterBar: 'border-border/60',
    tableFooterBarAttached: 'border-t border-border/60',
    tableRow: '[&>*]:border-border/50',
    tableEmptyRow: '[&>*]:border-border/50',
    tableLoadingRow: '[&>*]:border-border/50',
    layoutRoot: 'border-border/60',
    layoutPaneBordered: 'border-border/60',
  },
  solid: {},
  strong: {
    buttonDefault: 'border-foreground/20',
    badgeDefault: 'border-foreground/20',
    panelRoot: 'border-foreground/20',
    panelFooter: 'border-foreground/20',
    stateRoot: 'border-foreground/20',
    controlWrapper: 'border-foreground/20',
    selectMenu: 'border-foreground/20',
    switchTrack: 'border-foreground/20',
    checkboxBox: 'border-foreground/20',
    radioOption: 'border-foreground/20',
    radioMarker: 'border-foreground/20',
    dialogPanel: 'border-foreground/20',
    dialogHeader: 'border-foreground/15',
    dialogFooter: 'border-foreground/15',
    dropdownMenu: 'border-foreground/20',
    tooltipContent: 'border-foreground/20',
    noticeDefault: 'border-foreground/20',
    dividerRoot: 'border-foreground/20',
    tabsList: 'border-foreground/20',
    tabsPanel: 'border-foreground/20',
    segmentedRoot: 'border-foreground/20',
    avatarRoot: 'border-foreground/20',
    toolbarRoot: 'border-foreground/20',
    paginationButton: 'border-foreground/20',
    descriptionListItem: 'border-foreground/20',
    accordionItem: 'border-foreground/20',
    accordionPanel: 'border-foreground/20',
    menuContent: 'border-foreground/20',
    menuSeparator: 'bg-foreground/15',
    menuSubContent: 'border-foreground/20',
    tableContainer:
      'border-foreground/20 [--base-table-header-separator:hsl(var(--foreground)_/_0.15)]',
    tableHeader: 'border-foreground/20',
    tableFooter: '[&_tr>*]:border-foreground/20',
    tableFooterBar: 'border-foreground/20',
    tableFooterBarAttached: 'border-t border-foreground/20',
    tableRow: '[&>*]:border-foreground/15',
    tableEmptyRow: '[&>*]:border-foreground/15',
    tableLoadingRow: '[&>*]:border-foreground/15',
    layoutRoot: 'border-foreground/20',
    layoutPaneBordered: 'border-foreground/20',
    layoutResizeHandle: 'hover:bg-foreground/15 active:bg-foreground/20 focus-visible:bg-foreground/15',
  },
};

const backgroundSlots: Record<BaseBackground, Partial<BaseThemeSlots>> = {
  plain: {},
  muted: {
    buttonDefault: 'bg-muted/20 hover:bg-muted/45',
    panelRoot: 'bg-card',
    panelFooter: 'bg-muted/20',
    stateRoot: 'bg-muted/20',
    controlWrapper: 'bg-background',
    selectMenu: 'bg-background',
    selectOptionActive: 'bg-muted/70 text-foreground',
    checkboxBox: 'bg-background',
    radioOption: 'bg-card hover:bg-muted/45',
    radioMarker: 'bg-background',
    dialogPanel: 'bg-card',
    dropdownMenu: 'bg-background',
    dropdownItem: 'hover:bg-muted/60',
    tooltipContent: 'bg-popover',
    noticeDefault: 'bg-muted/20',
    tabsList: 'bg-muted/35',
    tabsTriggerActive: 'bg-background',
    tabsPanel: 'bg-card',
    segmentedRoot: 'bg-muted/35',
    segmentedItemActive: 'bg-background',
    progressTrack: 'bg-muted/60',
    skeletonRoot: 'bg-muted',
    toolbarRoot: 'bg-card',
    paginationButton: 'bg-background hover:bg-muted/60',
    paginationPage: 'bg-muted/60',
    descriptionListItem: 'bg-card',
    accordionItem: 'bg-card',
    menuContent: 'bg-background',
    menuItem: 'focus:bg-muted/70 data-[highlighted]:bg-muted/70',
    menuSubTrigger:
      'focus:bg-muted/70 data-[highlighted]:bg-muted/70 data-[state=open]:bg-muted/70',
    menuSubContent: 'bg-background',
    tableContainer: 'bg-background/95 [scrollbar-gutter:stable]',
    tableFooter: 'bg-muted/35',
    tableFooterBar: 'bg-muted/30 shadow-none',
    tableRowInteractive: 'cursor-pointer [--base-table-row-hover-bg:hsl(var(--muted)_/_0.45)]',
    tableLoadingSkeleton: 'bg-muted',
    layoutRoot: 'bg-card',
    layoutPane: 'bg-card',
    layoutPaneMuted: 'bg-muted/35',
    layoutResizeHandle: 'hover:bg-border/70 active:bg-border/80 focus-visible:bg-border/70',
  },
  soft: {
    buttonDefault: 'bg-muted/30 hover:bg-muted/55',
    panelRoot: 'bg-background/95 shadow-sm',
    stateRoot: 'bg-background/95 shadow-sm',
    controlWrapper: 'bg-background/95',
    selectMenu: 'bg-background/95 shadow-xl',
    selectOptionActive: 'bg-muted/60 text-foreground',
    checkboxBox: 'bg-background/95',
    radioOption: 'bg-background/95 shadow-sm hover:bg-muted/35',
    radioMarker: 'bg-background/95',
    dialogPanel: 'bg-background/95 shadow-xl',
    dropdownMenu: 'bg-background/95 shadow-xl',
    dropdownItem: 'hover:bg-muted/55',
    tooltipContent: 'bg-popover shadow-xl',
    noticeDefault: 'bg-background/95 shadow-sm',
    tabsList: 'bg-background/60 shadow-sm',
    tabsTriggerActive: 'bg-background',
    tabsPanel: 'bg-background/95 shadow-sm',
    segmentedRoot: 'bg-background/60 shadow-sm',
    segmentedItemActive: 'bg-background',
    skeletonRoot: 'bg-background/80',
    toolbarRoot: 'bg-background/95 shadow-sm',
    paginationButton: 'bg-background/95 hover:bg-muted/55',
    descriptionListItem: 'bg-background/95 shadow-sm',
    accordionItem: 'bg-background/95 shadow-sm',
    menuContent: 'bg-background/95 shadow-xl',
    menuItem: 'focus:bg-muted/60 data-[highlighted]:bg-muted/60',
    menuSubTrigger:
      'focus:bg-muted/60 data-[highlighted]:bg-muted/60 data-[state=open]:bg-muted/60',
    menuSubContent: 'bg-background/95 shadow-xl',
    tableContainer:
      'bg-background/95 shadow-sm [scrollbar-gutter:stable]',
    tableFooter: 'bg-muted/25',
    tableFooterBar: 'bg-muted/25 shadow-none',
    tableRowInteractive: 'cursor-pointer [--base-table-row-hover-bg:hsl(var(--muted)_/_0.35)]',
    tableLoadingSkeleton: 'bg-muted/65',
    layoutRoot: 'bg-background/95 shadow-sm',
    layoutPane: 'bg-background/95 shadow-sm',
    layoutPaneSoft: 'bg-background/95 shadow-md',
  },
  glass: {
    buttonBase: 'backdrop-blur-md',
    buttonDefault:
      'border-white/10 bg-white/[0.08] text-foreground hover:bg-white/[0.14]',
    badgeDefault: 'border-white/10 bg-white/[0.08] text-foreground',
    panelRoot: 'border-white/10 bg-white/[0.06] shadow-none backdrop-blur-md',
    panelFooter: 'border-white/10 bg-white/[0.05]',
    stateRoot: 'border-white/10 bg-white/[0.06] shadow-none backdrop-blur-md',
    stateIcon: 'bg-white/10 text-foreground',
    controlWrapper:
      'border-white/10 bg-white/[0.08] text-foreground backdrop-blur-md',
    selectMenu:
      'border-foreground/10 bg-background/10 text-foreground shadow-[0_18px_60px_hsl(var(--foreground)/0.18)] backdrop-blur-md',
    selectOption: 'text-foreground',
    selectOptionActive: 'bg-white/[0.14] text-foreground',
    selectOptionSelected: 'bg-white/[0.18] text-foreground',
    switchTrack: 'border-white/10 bg-white/[0.12] backdrop-blur-md',
    checkboxBox: 'border-white/10 bg-white/[0.08] backdrop-blur-md',
    checkboxBoxChecked: 'border-white/20 bg-white/[0.22] text-foreground',
    radioOption: 'border-white/10 bg-white/[0.06] backdrop-blur-md hover:bg-white/[0.10]',
    radioMarker: 'border-white/10 bg-white/[0.08] backdrop-blur-md',
    radioMarkerChecked: 'border-white/25 bg-white/[0.16]',
    dialogOverlay: 'bg-background/45 backdrop-blur-md',
    dialogPanel: 'border-white/10 bg-white/[0.08] shadow-2xl backdrop-blur-md',
    dialogHeader: 'border-white/10',
    dialogFooter: 'border-white/10',
    dropdownMenu:
      'border-foreground/10 bg-background/10 text-foreground shadow-[0_18px_60px_hsl(var(--foreground)/0.18)] backdrop-blur-md',
    dropdownItem: 'text-foreground hover:bg-white/[0.14]',
    tooltipContent:
      'border-foreground/10 bg-background/15 text-foreground shadow-[0_18px_60px_hsl(var(--foreground)/0.18)] backdrop-blur-md',
    noticeDefault:
      'border-white/10 bg-white/[0.08] text-foreground backdrop-blur-md',
    dividerRoot: 'border-white/15',
    tabsList: 'border-white/10 bg-white/[0.08] backdrop-blur-md',
    tabsTrigger: 'text-muted-foreground hover:bg-white/[0.12] hover:text-foreground',
    tabsTriggerActive: 'bg-white/[0.16] text-foreground shadow-none',
    tabsPanel:
      'border-white/10 bg-white/[0.06] shadow-none backdrop-blur-md',
    segmentedRoot: 'border-white/10 bg-white/[0.08] backdrop-blur-md',
    segmentedItem: 'text-muted-foreground hover:bg-white/[0.12] hover:text-foreground',
    segmentedItemActive: 'bg-white/[0.16] text-foreground shadow-none',
    progressTrack: 'bg-white/[0.12]',
    skeletonRoot: 'bg-white/[0.16]',
    avatarRoot: 'border-white/10 bg-white/[0.12] text-foreground',
    avatarFallback: 'bg-white/[0.12] text-foreground',
    avatarStatus: 'border-background',
    toolbarRoot:
      'border-white/10 bg-white/[0.06] shadow-none backdrop-blur-md',
    paginationButton:
      'border-white/10 bg-white/[0.08] text-foreground hover:bg-white/[0.14]',
    paginationPage: 'bg-white/[0.12] text-foreground',
    descriptionListItem:
      'border-white/10 bg-white/[0.06] shadow-none backdrop-blur-md',
    accordionItem:
      'border-white/10 bg-white/[0.06] shadow-none backdrop-blur-md',
    accordionPanel: 'border-white/10',
    menuContent:
      'border-foreground/10 bg-background/10 text-foreground shadow-[0_18px_60px_hsl(var(--foreground)/0.18)] backdrop-blur-md',
    menuSeparator: 'bg-white/10',
    menuItem:
      'text-foreground focus:bg-white/[0.14] focus:text-foreground data-[highlighted]:bg-white/[0.14] data-[highlighted]:text-foreground',
    menuSubTrigger:
      'text-foreground focus:bg-white/[0.14] focus:text-foreground data-[highlighted]:bg-white/[0.14] data-[highlighted]:text-foreground data-[state=open]:bg-white/[0.16]',
    menuSubContent:
      'border-foreground/10 bg-background/10 text-foreground shadow-[0_18px_60px_hsl(var(--foreground)/0.18)] backdrop-blur-md',
    tableContainer:
      'border-foreground/10 bg-background/[0.06] shadow-none backdrop-blur-md [scrollbar-gutter:stable] [--base-table-sticky-header-bg:hsl(var(--background)/0.94)] [--base-table-header-separator:hsl(var(--foreground)/0.24)]',
    tableHeader: 'border-white/10',
    tableFooter: 'bg-white/[0.05] [&_tr>*]:border-white/10',
    tableFooterBar:
      'border-white/10 bg-white/[0.05] shadow-none backdrop-blur-md',
    tableFooterBarAttached: 'border-t border-white/10',
    tableRow: '[&>*]:border-white/10',
    tableRowInteractive: 'cursor-pointer [--base-table-row-hover-bg:hsl(var(--foreground)/0.08)]',
    tableHeadLabel: 'text-foreground',
    tableLoadingSkeleton: 'bg-white/[0.16]',
    layoutRoot:
      'border-white/10 bg-white/[0.05] shadow-none backdrop-blur-md',
    layoutPane:
      'border-white/10 bg-white/[0.06] shadow-none backdrop-blur-md',
    layoutPaneMuted: 'bg-white/[0.08]',
    layoutPaneSoft: 'bg-white/[0.10] shadow-none backdrop-blur-md',
    layoutResizeHandle: 'text-foreground/65 hover:bg-white/10 active:bg-white/15 focus-visible:bg-white/10',
    layoutResizeHandleIndicator: 'bg-white/45 group-hover/resize:bg-white/75',
  },
  dark: {
    buttonBase:
      'border-white/15 text-white shadow-lg backdrop-blur',
    buttonDefault: 'bg-black/30 hover:bg-black/[0.45]',
    buttonPrimary: 'border-white/25 bg-white/20 text-white hover:bg-white/25',
    buttonMuted:
      'border-white/10 bg-black/20 text-white/70 hover:bg-black/[0.35] hover:text-white',
    badgeDefault: 'border-white/10 bg-white/[0.08] text-white',
    badgeMuted: 'border-white/10 bg-black/20 text-white/70',
    panelRoot: 'border-white/10 bg-slate-950 text-white shadow-none',
    panelTitle: 'text-white',
    panelDescription: 'text-white/65',
    panelBody: 'text-white/85',
    panelFooter: 'border-white/10 bg-white/[0.04]',
    stateRoot: 'border-white/10 bg-slate-950 text-white shadow-none',
    stateIcon: 'bg-white/10 text-white/75',
    stateTitle: 'text-white',
    stateDescription: 'text-white/65',
    fieldLabel: 'text-white',
    fieldDescription: 'text-white/65',
    fieldMessage: 'text-white/65',
    controlWrapper:
      'border-white/15 bg-black/30 text-white shadow-lg backdrop-blur focus-within:border-white/35',
    controlBase: 'text-white placeholder:text-white/45',
    selectChevron: 'text-white/65',
    selectMenu: 'border-white/15 bg-slate-950 text-white shadow-2xl',
    selectOption: 'text-white',
    selectOptionActive: 'bg-white/[0.12] text-white',
    selectOptionSelected: 'bg-white/[0.16] text-white',
    selectPlaceholder: 'text-white/45',
    switchTrack: 'border-white/15 bg-black/30 shadow-lg backdrop-blur',
    switchChecked: 'border-white/25 bg-white/25 text-white',
    switchLabel: 'text-white',
    switchDescription: 'text-white/65',
    checkboxBox: 'border-white/15 bg-black/30 text-white shadow-lg backdrop-blur',
    checkboxBoxChecked: 'border-white/25 bg-white/25 text-white',
    checkboxLabel: 'text-white',
    checkboxDescription: 'text-white/65',
    radioOption: 'border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.08]',
    radioMarker: 'border-white/15 bg-black/30 text-white shadow-lg backdrop-blur',
    radioMarkerChecked: 'border-white/25 bg-white/25',
    radioIndicator: 'bg-white',
    radioLabel: 'text-white',
    radioDescription: 'text-white/65',
    dialogOverlay: 'bg-slate-950/75',
    dialogPanel: 'border-white/10 bg-slate-950 text-white shadow-2xl',
    dialogHeader: 'border-white/10',
    dialogTitle: 'text-white',
    dialogDescription: 'text-white/65',
    dialogBody: 'text-white/85',
    dialogFooter: 'border-white/10',
    dialogClose: 'text-white/65 hover:bg-white/[0.08] hover:text-white',
    dropdownMenu: 'border-white/15 bg-slate-950 text-white shadow-2xl',
    dropdownLabel: 'text-white/55',
    dropdownSeparator: 'bg-white/15',
    dropdownItem: 'text-white hover:bg-white/[0.10]',
    dropdownItemDescription: 'text-white/55',
    dropdownItemShortcut: 'text-white/55',
    tooltipContent: 'border-white/15 bg-slate-950 text-white shadow-2xl',
    noticeDefault: 'border-white/10 bg-white/[0.08] text-white',
    noticeMuted: 'border-white/10 bg-black/20 text-white/70',
    dividerRoot: 'border-white/15',
    dividerLabel: 'text-white/55',
    sectionHeaderEyebrow: 'text-white/55',
    sectionHeaderTitle: 'text-white',
    sectionHeaderDescription: 'text-white/65',
    tabsList: 'border-white/10 bg-white/[0.08]',
    tabsTrigger: 'text-white/65 hover:bg-white/[0.12] hover:text-white',
    tabsTriggerActive: 'bg-white/[0.16] text-white shadow-none',
    tabsPanel: 'border-white/10 bg-white/[0.06] text-white',
    segmentedRoot: 'border-white/10 bg-white/[0.08]',
    segmentedItem: 'text-white/65 hover:bg-white/[0.12] hover:text-white',
    segmentedItemActive: 'bg-white/[0.16] text-white shadow-none',
    progressTrack: 'bg-white/[0.12]',
    progressLabel: 'text-white',
    progressValue: 'text-white/65',
    skeletonRoot: 'bg-white/[0.14]',
    avatarRoot: 'border-white/15 bg-white/[0.10] text-white',
    avatarFallback: 'bg-white/[0.10] text-white/75',
    avatarText: 'text-white',
    avatarStatus: 'border-slate-950',
    toolbarRoot: 'border-white/10 bg-white/[0.06] text-white',
    toolbarTitle: 'text-white',
    toolbarDescription: 'text-white/65',
    breadcrumbLink: 'text-white/65 hover:text-white',
    breadcrumbCurrent: 'text-white',
    breadcrumbSeparator: 'text-white/45',
    paginationInfo: 'text-white/65',
    paginationButton: 'border-white/10 bg-white/[0.08] text-white hover:bg-white/[0.14]',
    paginationPage: 'bg-white/[0.14] text-white',
    descriptionListItem: 'border-white/10 bg-white/[0.06]',
    descriptionListTerm: 'text-white/55',
    descriptionListValue: 'text-white',
    accordionItem: 'border-white/10 bg-white/[0.06]',
    accordionTrigger: 'text-white hover:bg-white/[0.08]',
    accordionPanel: 'border-white/10 text-white/70',
    tableContainer:
      'border-foreground/10 bg-background text-foreground shadow-none [scrollbar-gutter:stable] [--base-table-sticky-header-bg:hsl(var(--background)/0.98)] [--base-table-header-separator:hsl(var(--foreground)/0.16)]',
    tableRoot: 'text-white',
    tableHeader: 'border-white/10',
    tableFooter: 'bg-white/[0.05] [&_tr>*]:border-white/10',
    tableFooterBar: 'border-white/10 bg-white/[0.05] text-white/65',
    tableFooterBarAttached: 'border-t border-white/10',
    tableRow: '[&>*]:border-white/10',
    tableRowInteractive: 'cursor-pointer [--base-table-row-hover-bg:hsl(var(--foreground)/0.06)]',
    tableHead: 'text-white/65',
    tableHeadLabel: 'text-white',
    tableHeadDescription: 'text-white/55',
    tableHeadMeta: 'text-white/55',
    tableHeadIcon: 'text-white/55',
    tableHeadSortIcon: 'text-white/55',
    tableHeadDragHandle: 'text-white/55 hover:bg-white/[0.08]',
    tableHeadResizeHandle: 'after:bg-white/20 hover:after:bg-white/60',
    tableCell: 'text-white/85',
    tableEmptyIcon: 'bg-white/10 text-white/65',
    tableEmptyTitle: 'text-white',
    tableEmptyDescription: 'text-white/60',
    menuContent: 'border-white/10 bg-slate-950 text-white shadow-2xl',
    menuLabel: 'text-white/55',
    menuSeparator: 'bg-white/10',
    menuItem:
      'text-white focus:bg-white/[0.10] focus:text-white data-[highlighted]:bg-white/[0.10] data-[highlighted]:text-white',
    menuItemDefault: 'text-white',
    menuItemPrimary: 'text-primary focus:text-primary data-[highlighted]:text-primary',
    menuItemSuccess:
      'text-[hsl(var(--success,142_69%_58%))] focus:text-[hsl(var(--success,142_69%_58%))] data-[highlighted]:text-[hsl(var(--success,142_69%_58%))]',
    menuItemWarning:
      'text-[hsl(var(--warning-foreground,53_98%_77%))] focus:text-[hsl(var(--warning-foreground,53_98%_77%))] data-[highlighted]:text-[hsl(var(--warning-foreground,53_98%_77%))]',
    menuItemDanger:
      'text-destructive-text focus:text-destructive-text data-[highlighted]:text-destructive-text',
    menuItemMuted:
      'text-white/65 focus:text-white data-[highlighted]:text-white',
    menuItemShortcut: 'text-white/45',
    menuSubTrigger:
      'text-white focus:bg-white/[0.10] focus:text-white data-[highlighted]:bg-white/[0.10] data-[highlighted]:text-white data-[state=open]:bg-white/[0.12]',
    menuSubTriggerIcon: 'text-white/55',
    menuSubContent: 'border-white/10 bg-slate-950 text-white shadow-2xl',
    tableLoadingSkeleton: 'bg-white/[0.14]',
    layoutRoot: 'border-white/10 bg-slate-950 text-white shadow-none',
    layoutPane: 'border-white/10 bg-slate-950 text-white shadow-none',
    layoutPaneMuted: 'bg-white/[0.06]',
    layoutPaneSoft: 'bg-white/[0.08] shadow-none',
    layoutResizeHandle: 'text-white/65 hover:bg-white/10 active:bg-white/15 focus-visible:bg-white/10',
    layoutResizeHandleIndicator: 'bg-white/45 group-hover/resize:bg-white/75',
  },
};

const presetDefaults: Record<
  BaseThemePresetId,
  Pick<BaseAppearance, 'density' | 'radius' | 'border' | 'background'>
> = {
  default: {
    density: 'default',
    radius: 'md',
    border: 'solid',
    background: 'plain',
  },
  soft: {
    density: 'default',
    radius: 'lg',
    border: 'subtle',
    background: 'soft',
  },
  compact: {
    density: 'compact',
    radius: 'md',
    border: 'subtle',
    background: 'muted',
  },
  'tinted-glass': {
    density: 'default',
    radius: 'lg',
    border: 'subtle',
    background: 'glass',
  },
  glass: {
    density: 'default',
    radius: 'xl',
    border: 'subtle',
    background: 'glass',
  },
  'hard-edge': {
    density: 'compact',
    radius: 'none',
    border: 'strong',
    background: 'plain',
  },
};

const presetSlots: Record<string, Partial<BaseThemeSlots>> = {
  soft: {
    buttonBase: 'font-medium',
    panelRoot: 'border-border/70',
    controlWrapper: 'font-medium',
    tabsTrigger: 'font-medium',
    segmentedItem: 'font-medium',
    toolbarRoot: 'border-border/70',
    tableContainer: 'border-border/70',
    tableFooterBar: 'border-border/70',
    tableHeadLabel: 'font-semibold',
    menuItemLabel: 'font-medium',
  },
  compact: {
    buttonBase: 'text-xs',
    badgeBase: 'text-[11px]',
    fieldLabel: 'text-xs',
    controlBase: 'text-xs',
    selectOption: 'text-xs',
    menuItem: 'text-xs',
    menuSubTrigger: 'text-xs',
    noticeBase: 'text-xs',
    sectionHeaderTitle: 'text-sm',
    tabsTrigger: 'text-xs',
    segmentedItem: 'text-xs',
    toolbarTitle: 'text-xs',
    paginationRoot: 'text-xs',
  },
  'tinted-glass': {
    buttonPrimary: 'border-primary/25 bg-primary/15 text-primary-foreground hover:bg-primary/20',
    badgePrimary: 'border-primary/25 bg-primary/15 text-primary-foreground',
    noticePrimary: 'border-primary/25 bg-primary/15 text-primary-foreground',
    progressIndicator: 'bg-primary',
    avatarStatus: 'border-background',
  },
  glass: {
    panelRoot: 'shadow-[0_24px_80px_hsl(var(--foreground)/0.12)]',
    stateRoot: 'shadow-[0_24px_80px_hsl(var(--foreground)/0.12)]',
    noticeBase: 'shadow-[0_18px_60px_hsl(var(--foreground)/0.10)]',
    tabsPanel: 'shadow-[0_18px_60px_hsl(var(--foreground)/0.10)]',
    toolbarRoot: 'shadow-[0_18px_60px_hsl(var(--foreground)/0.10)]',
    descriptionListItem: 'shadow-[0_12px_40px_hsl(var(--foreground)/0.08)]',
    accordionItem: 'shadow-[0_12px_40px_hsl(var(--foreground)/0.08)]',
  },
  'hard-edge': {
    buttonBase: 'shadow-none',
    panelRoot: 'shadow-none',
    stateRoot: 'shadow-none',
    controlWrapper: 'shadow-none',
    selectMenu: 'shadow-none',
    menuContent: 'shadow-none',
    menuSubContent: 'shadow-none',
    noticeBase: 'shadow-none',
    tabsList: 'shadow-none',
    tabsTriggerActive: 'shadow-none',
    tabsPanel: 'shadow-none',
    segmentedRoot: 'shadow-none',
    segmentedItemActive: 'shadow-none',
    toolbarRoot: 'shadow-none',
    descriptionListItem: 'shadow-none',
    accordionItem: 'shadow-none',
  },
};

function getPresetDefaults(preset: BaseThemePresetId) {
  return (
    presetDefaults[preset as keyof typeof presetDefaults] ?? presetDefaults.default
  );
}

const slotKeys = Object.keys(baseSlots) as Array<keyof BaseThemeSlots>;

function mergeSlots(
  ...sources: Array<Partial<BaseThemeSlots> | undefined>
): ResolvedSlots {
  return slotKeys.reduce((resolved, key) => {
    resolved[key] = cn(...sources.map((source) => source?.[key]));
    return resolved;
  }, {} as ResolvedSlots);
}

function mergeStyles(...styleSets: Array<BaseThemeStyles | undefined>) {
  return Object.assign({}, ...styleSets.filter(Boolean));
}

export function resolveBaseAppearance(
  appearance?: BaseAppearance,
): BaseResolvedTheme {
  const preset = appearance?.preset ?? 'default';
  const variant = appearance?.variant ?? 'default';
  const defaults = getPresetDefaults(preset);
  const density = appearance?.density ?? defaults.density ?? 'default';
  const state = appearance?.state ?? 'idle';
  const radius = appearance?.radius ?? defaults.radius ?? 'md';
  const border = appearance?.border ?? defaults.border ?? 'solid';
  const background = appearance?.background ?? defaults.background ?? 'plain';

  return {
    preset,
    variant,
    density,
    state,
    radius,
    border,
    background,
    styles: mergeStyles(emptyStyles, appearance?.styles),
    slots: mergeSlots(
      baseSlots,
      presetSlots[preset],
      densitySlots[density],
      radiusSlots[radius],
      borderSlots[border],
      backgroundSlots[background],
      appearance?.slots,
    ),
  };
}

export function getBaseButtonToneClassName(
  theme: BaseResolvedTheme,
  tone: BaseControlTone = 'default',
  active = false,
) {
  return cn(
    tone === 'default' && theme.slots.buttonDefault,
    tone === 'primary' && theme.slots.buttonPrimary,
    tone === 'success' && theme.slots.buttonSuccess,
    tone === 'warning' && theme.slots.buttonWarning,
    tone === 'danger' && theme.slots.buttonDanger,
    tone === 'muted' && theme.slots.buttonMuted,
    active && theme.slots.buttonActive,
  );
}

export function getBaseBadgeToneClassName(
  theme: BaseResolvedTheme,
  tone: BaseControlTone = 'default',
) {
  return cn(
    tone === 'default' && theme.slots.badgeDefault,
    tone === 'primary' && theme.slots.badgePrimary,
    tone === 'success' && theme.slots.badgeSuccess,
    tone === 'warning' && theme.slots.badgeWarning,
    tone === 'danger' && theme.slots.badgeDanger,
    tone === 'muted' && theme.slots.badgeMuted,
  );
}

export function getBaseNoticeToneClassName(
  theme: BaseResolvedTheme,
  tone: BaseControlTone = 'default',
) {
  return cn(
    tone === 'default' && theme.slots.noticeDefault,
    tone === 'primary' && theme.slots.noticePrimary,
    tone === 'success' && theme.slots.noticeSuccess,
    tone === 'warning' && theme.slots.noticeWarning,
    tone === 'danger' && theme.slots.noticeDanger,
    tone === 'muted' && theme.slots.noticeMuted,
  );
}

export function getBaseMenuItemToneClassName(
  theme: BaseResolvedTheme,
  tone: BaseControlTone = 'default',
) {
  return cn(
    tone === 'default' && theme.slots.menuItemDefault,
    tone === 'primary' && theme.slots.menuItemPrimary,
    tone === 'success' && theme.slots.menuItemSuccess,
    tone === 'warning' && theme.slots.menuItemWarning,
    tone === 'danger' && theme.slots.menuItemDanger,
    tone === 'muted' && theme.slots.menuItemMuted,
  );
}

export function getBaseTypographyToneClassName(
  theme: BaseResolvedTheme,
  tone: BaseTypographyTone = 'default',
) {
  return cn(
    tone === 'default' && theme.slots.typographyToneDefault,
    tone === 'muted' && theme.slots.typographyToneMuted,
    tone === 'primary' && theme.slots.typographyTonePrimary,
    tone === 'success' && theme.slots.typographyToneSuccess,
    tone === 'warning' && theme.slots.typographyToneWarning,
    tone === 'danger' && theme.slots.typographyToneDanger,
    tone === 'inherit' && theme.slots.typographyToneInherit,
  );
}

export function getBaseTextSizeClassName(
  theme: BaseResolvedTheme,
  size: BaseTypographySize = 'md',
) {
  return cn(
    size === 'xs' && theme.slots.textSizeXs,
    size === 'sm' && theme.slots.textSizeSm,
    size === 'md' && theme.slots.textSizeMd,
    size === 'lg' && theme.slots.textSizeLg,
    size === 'xl' && theme.slots.textSizeXl,
  );
}

export function getBaseHeadingSizeClassName(
  theme: BaseResolvedTheme,
  size: BaseHeadingSize = 'lg',
) {
  return cn(
    size === 'xs' && theme.slots.headingSizeXs,
    size === 'sm' && theme.slots.headingSizeSm,
    size === 'md' && theme.slots.headingSizeMd,
    size === 'lg' && theme.slots.headingSizeLg,
    size === 'xl' && theme.slots.headingSizeXl,
    size === '2xl' && theme.slots.headingSize2xl,
  );
}

export function getBaseListSpacingClassName(
  theme: BaseResolvedTheme,
  spacing: BaseListSpacing = 'default',
) {
  return cn(
    spacing === 'compact' && theme.slots.listSpacingCompact,
    spacing === 'default' && theme.slots.listSpacingDefault,
    spacing === 'relaxed' && theme.slots.listSpacingRelaxed,
  );
}

export function getBaseListMarkerClassName(
  theme: BaseResolvedTheme,
  marker: Exclude<BaseListMarker, 'auto'>,
) {
  return cn(
    marker === 'none' && theme.slots.listMarkerNone,
    marker === 'disc' && theme.slots.listMarkerDisc,
    marker === 'circle' && theme.slots.listMarkerCircle,
    marker === 'square' && theme.slots.listMarkerSquare,
    marker === 'decimal' && theme.slots.listMarkerDecimal,
    marker === 'lower-alpha' && theme.slots.listMarkerLowerAlpha,
    marker === 'upper-alpha' && theme.slots.listMarkerUpperAlpha,
  );
}

const cssLengthPattern = /^(?:0+(?:\.0+)?|(?:\d+(?:\.\d+)?|\.\d+)(?:%|cap|ch|cm|dvb|dvh|dvi|dvw|em|ex|ic|in|lh|lvb|lvh|lvi|lvw|mm|pc|pt|px|q|rem|rlh|svb|svh|svi|svw|vb|vh|vi|vmax|vmin|vw))$/i;
const cssLengthFunctions = new Set(['calc', 'clamp', 'max', 'min', 'var']);
const unsafeCssValuePattern = /[;{}<>"'\\\u0000-\u001f\u007f]/;
const gridTemplateTokenPattern = /(?:\b(?:auto|fit-content|max-content|min-content|subgrid)\b|(?:calc|clamp|max|min|minmax|repeat|var)\s*\(|(?:^|[\s,(])(?:0+(?:\.0+)?|(?:\d+(?:\.\d+)?|\.\d+)(?:%|cap|ch|cm|dvb|dvh|dvi|dvw|em|ex|fr|ic|in|lh|lvb|lvh|lvi|lvw|mm|pc|pt|px|q|rem|rlh|svb|svh|svi|svw|vb|vh|vi|vmax|vmin|vw))(?:$|[\s,)\]]))/i;

function hasBalancedDelimiters(value: string) {
  const pairs: Record<string, string> = { ')': '(', ']': '[' };
  const stack: string[] = [];

  for (const character of value) {
    if (character === '(' || character === '[') {
      stack.push(character);
    } else if (character === ')' || character === ']') {
      if (stack.pop() !== pairs[character]) return false;
    }
  }

  return stack.length === 0;
}

function isSupportedCssLengthFunction(value: string) {
  const match = /^([a-z]+)\(([\s\S]+)\)$/i.exec(value);
  if (!match || !cssLengthFunctions.has(match[1].toLowerCase())) return false;
  if (!match[2].trim() || !hasBalancedDelimiters(value)) return false;

  if (match[1].toLowerCase() === 'var') {
    return /^\s*--[a-z0-9_-]+(?:\s*,[\s\S]+)?\s*$/i.test(match[2]);
  }

  return cssLengthPattern.test(match[2].trim()) || /\b(?:calc|clamp|max|min|var)\s*\(/i.test(match[2]) || /(?:^|[\s,(])(?:0+(?:\.0+)?|(?:\d+(?:\.\d+)?|\.\d+)(?:%|cap|ch|cm|dvb|dvh|dvi|dvw|em|ex|ic|in|lh|lvb|lvh|lvi|lvw|mm|pc|pt|px|q|rem|rlh|svb|svh|svi|svw|vb|vh|vi|vmax|vmin|vw))(?:$|[\s,)])/i.test(match[2]);
}

export function resolveBaseLayoutLength(
  componentName: string,
  propName: string,
  value: BaseLayoutLength,
) {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) {
      throw new RangeError(
        `${componentName} ${propName} must be a finite, non-negative number or a supported CSS length string.`,
      );
    }
    return `${value}px`;
  }

  if (typeof value !== 'string') {
    throw new TypeError(
      `${componentName} ${propName} must be a finite, non-negative number or a supported CSS length string.`,
    );
  }

  const normalized = value.trim();
  if (
    !normalized ||
    unsafeCssValuePattern.test(normalized) ||
    (!cssLengthPattern.test(normalized) &&
      !isSupportedCssLengthFunction(normalized))
  ) {
    throw new TypeError(
      `${componentName} ${propName} must be a non-empty, non-negative CSS length (for example 12px, 1rem, 5%, or var(--space)).`,
    );
  }

  return normalized;
}

export function resolveBaseGridColumns(value: number | string) {
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value <= 0) {
      throw new RangeError(
        'BaseGrid columns must be a positive integer or a supported CSS grid-template-columns string.',
      );
    }
    return `repeat(${value}, minmax(0, 1fr))`;
  }

  if (typeof value !== 'string') {
    throw new TypeError(
      'BaseGrid columns must be a positive integer or a supported CSS grid-template-columns string.',
    );
  }

  const normalized = value.trim();
  if (
    !normalized ||
    unsafeCssValuePattern.test(normalized) ||
    /\burl\s*\(/i.test(normalized) ||
    !hasBalancedDelimiters(normalized) ||
    !gridTemplateTokenPattern.test(normalized)
  ) {
    throw new TypeError(
      'BaseGrid columns must be a non-empty CSS grid-template-columns value such as "12rem minmax(0, 1fr)" or "repeat(3, minmax(0, 1fr))".',
    );
  }

  return normalized;
}

type BaseLayoutAlignValue =
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'stretch'
  | 'baseline';

type BaseLayoutJustifyValue =
  | 'flex-start'
  | 'center'
  | 'flex-end'
  | 'space-between'
  | 'space-around'
  | 'space-evenly';

const layoutAlignValues: Record<BaseLayoutAlign, BaseLayoutAlignValue> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
};

const layoutJustifyValues: Record<
  BaseLayoutJustify,
  BaseLayoutJustifyValue
> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

export function resolveBaseLayoutAlign(
  componentName: string,
  value: BaseLayoutAlign,
): BaseLayoutAlignValue {
  if (!Object.prototype.hasOwnProperty.call(layoutAlignValues, value)) {
    throw new TypeError(
      `${componentName} align must be one of: start, center, end, stretch, baseline.`,
    );
  }
  return layoutAlignValues[value];
}

export function resolveBaseLayoutJustify(
  componentName: string,
  value: BaseLayoutJustify,
): BaseLayoutJustifyValue {
  if (!Object.prototype.hasOwnProperty.call(layoutJustifyValues, value)) {
    throw new TypeError(
      `${componentName} justify must be one of: start, center, end, between, around, evenly.`,
    );
  }
  return layoutJustifyValues[value];
}
